import { useState, useEffect } from "react";
import _ from "lodash";
import { useNavigate } from "react-router-dom";

import { BreadcrumbsCommon, HeartCommon, StyledButton, StyledFavoritesPage } from "@components";
import { AppModuleLayout } from "@layouts";
import { PATH_FAVORITES, PATH_HOME } from "@routes";
import {
    fetchFavoriteTours,
    selectFavoriteTours,
    updateFavorite,
    useAppDispatch,
    useAppSelector,
} from "@redux";
import { IDetailTour, IProduct } from "@interfaces";
import styled from "styled-components";
import { breakpoint, theme } from "@utilities";
import useImageFallback from "src/Utilities/imageFallbackHelper";
import { Link } from "react-router-dom";
import { formatProductResponse } from "src/Module/Checkout/productHelper";

const FavoriteTourItem = ({
    tour,
    loading,
    onUpdateFavorite,
}: {
    tour: IDetailTour;
    loading: boolean;
    onUpdateFavorite: (productId: string) => void;
}) => {
    const imageSrc = useImageFallback({
        productImagePath: tour.json?.productImagePath,
        productSmallToMediumSizeImagePath: tour.json?.productSmallToMediumSizeImagePath,
        productLargeSizeImagePath: tour.json?.productLargeSizeImagePath,
    });

    let duringTime = "";
    const dDays = Number(tour.json?.durationDays);
    const dNights = Number(tour.json?.durationNight);
    if (dDays > 0) duringTime += `${dDays} ${dDays === 1 ? "day" : "days"}`;
    if (dDays > 0 && dNights > 0) duringTime += ", ";
    if (dNights > 0) duringTime += `${dNights} ${dNights === 1 ? "night" : "nights"}`;

    const favoritesProducts = formatProductResponse(
        tour.json as unknown as IProduct,
        tour.tdms_product_id?.toString() ?? "",
    );

    return (
        <StyledSavedCartWrapper
            to={`/detail-tour/${favoritesProducts.data.productId}`}
            key={tour.tdms_product_id}
        >
            <ImageContainer>
                <div className="tour_image" style={{ backgroundImage: `url(${imageSrc})` }} />
            </ImageContainer>
            <StyledTopSection>
                <StyledTitle>{favoritesProducts.data.name}</StyledTitle>
                <StyledInfo>{favoritesProducts.data.location}</StyledInfo>
                <Separator />
                <StyledPrice>
                    <div className="destination__price">
                        <span className="from">From</span>
                        <span className="price">
                            ${Number(favoritesProducts.data.rrp).toFixed(2)}
                        </span>
                    </div>
                    <div className="destination__duration">
                        <div className="clock">
                            <img src="/images/fi_clock.svg" alt="clock" />
                        </div>
                        <span className="time">{duringTime}</span>
                    </div>
                </StyledPrice>
            </StyledTopSection>
            {/* prevent navigation when clicking the heart */}
            <IconContainer
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <HeartCommon
                    active={true}
                    loading={loading}
                    onClick={() =>
                        favoritesProducts.data.productId &&
                        onUpdateFavorite(favoritesProducts.data.productId)
                    }
                />
            </IconContainer>
        </StyledSavedCartWrapper>
    );
};

/**
 * The favourites listing + its data wiring, without the page chrome
 * (layout / breadcrumbs). Reused by the standalone Favourites page and by the
 * Favourites tab on the Profile page.
 */
export const FavoritesContent = ({ showHeading = true }: { showHeading?: boolean }) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    // pull favorites from redux
    const favoritesTour = useAppSelector(selectFavoriteTours).tours;
    const safeFavoritesTour = _.isArray(favoritesTour) ? favoritesTour : [];
    // track which item is loading (un-favoriting)
    const [loadingId, setLoadingId] = useState<string | null>(null);

    // fetch on mount
    useEffect(() => {
        dispatch(fetchFavoriteTours());
    }, [dispatch]);

    const handleUpdateFavorite = async (productId: string) => {
        setLoadingId(productId);
        try {
            await dispatch(updateFavorite({ productId, method: 0 }));
        } finally {
            setLoadingId(null);
        }
    };

    return (
        <div className="subWrapper">
            <div className="listProduct">
                {showHeading && (
                    <div className="heading">
                        <p>Favourites</p>
                    </div>
                )}
                {safeFavoritesTour.length > 0 ? (
                    safeFavoritesTour.map((tour) => (
                        <FavoriteTourItem
                            key={tour.productId}
                            tour={tour}
                            loading={loadingId === tour.productId?.toString()}
                            onUpdateFavorite={handleUpdateFavorite}
                        />
                    ))
                ) : (
                    <EmptyFavoritesWrapper>
                        <EmptyFavoritesTitle>
                            You don't have any favourites yet.
                        </EmptyFavoritesTitle>
                        <EmptyFavoritesSubtitle>
                            You'll find all your favourites here.
                        </EmptyFavoritesSubtitle>
                        <EmptyFavoritesButtonWrapper>
                            <ExploreTrip
                                color={theme.gradients.button}
                                onClick={() => navigate(PATH_HOME)}
                            >
                                Explore trips
                            </ExploreTrip>
                        </EmptyFavoritesButtonWrapper>
                    </EmptyFavoritesWrapper>
                )}
            </div>
        </div>
    );
};

// Main Favorites page
export const Favorites = () => {
    const breadcrumbsData = [
        { title: "Home", link: PATH_HOME },
        { title: "Favourites", link: PATH_FAVORITES },
    ];

    return (
        <AppModuleLayout>
            <StyledFavoritesPage>
                <div className="wrapper">
                    <div className="breadCrumbs">
                        <BreadcrumbsCommon data={breadcrumbsData} />
                    </div>
                    <FavoritesContent />
                </div>
            </StyledFavoritesPage>
        </AppModuleLayout>
    );
};
const StyledTitle = styled.div`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    width: 90%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    ${breakpoint.breakMobile`
        font-size: 14px;
        line-height: 150%;
    `}
`;

const StyledInfo = styled.div`
    font-size: 14px;
    color: #6e7375;
    width: 90%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    ${breakpoint.breakMobile`
        font-size: 12px;
    `}
`;
const ImageContainer = styled.div`
    display: flex;
`;

const IconContainer = styled.div`
    position: absolute;
    top: 14px;
    right: 12px;
`;
const StyledSavedCartWrapper = styled(Link)`
    position: relative;
    display: flex;
    gap: 12px;
    background-color: white;
    margin-bottom: 24px;
    border-radius: ${theme.borderRadius.medium};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    .tour_image {
        position: relative;
        background-repeat: no-repeat;
        background-position: center;
        border-radius: ${theme.borderRadius.medium};
        background-size: cover;
        min-width: 260px;
        height: 154px;
        ${breakpoint.breakMobile`
            min-width: 115px;
            height: 154px;
        `}
    }
`;
const StyledTopSection = styled.div`
    font-family: Poppins;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 12px 16px 0px;
    width: 100%;
`;

const Separator = styled.div`
    border-bottom: 1px solid #e6e9eb;
    margin: 8px 0;
    margin-top: auto;
`;

const StyledPrice = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    align-self: flex-end;
    margin-top: auto; 
    .destination__price{
        display: flex;
        align-items: center;
    }
    .from {
        font-size: 14px;
        font-family: Dm Sans;
        ${breakpoint.breakMobile`
            font-size: 12px;
        `}
    }
    .price {
        font-size: 16px;
        font-weight: 600;
        margin-left: 9px;
        font-family: Poppins;

        ${breakpoint.breakMobile`
            font-size: 12px;
        `}

        ${breakpoint.breakTablet`
            font-size: 20px;
            margin-left: 4px;
        `}

        ${breakpoint.breakIpadPro`
            font-size: 16px;
            margin-left: 7px;
        `}
        
        ${breakpoint.breakLarge`
            font-size: 20px;
            margin-left: 9px;
        `}
    }
    .destination__duration {
        display: flex;

        align-items: center;
        .clock{
            width: 16px;
            height: 16px;
            margin-right: 4px;

            img {
                width: 100%;
                height: 100%;
            }
        }
    }
        
        span {
            font-size: 14px;
            font-weight: 400;
            white-space: nowrap;

            ${breakpoint.breakMobile`
                    font-size: 10px;
                `}

            ${breakpoint.breakTablet`
                    font-size: 16px;
                `}

            ${breakpoint.breakIpadPro`
                font-size: 12px;
            `}
            
            ${breakpoint.breakLarge`
                font-size: 16px;
            `}
        }
    }
`;

export const ExploreTrip = styled(StyledButton)`
    font-size: 12px;
    height: 40px;
    margin: 0 2.5px;
    white-space: nowrap;
    padding: 0 10px;
    flex: 1;
    cursor: pointer;
    border-radius: ${theme.borderRadius.small};

    ${breakpoint.breakIpadPro`
        font-size: 12px;
        margin: 0 5px;
    `}

    ${breakpoint.breakLarge`
        height: 54px;
        font-size: 14px;
        margin: 0 8px;
    `}
`;

const EmptyFavoritesWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 350px;
    border-radius: ${theme.borderRadius.large};
    margin: 40px 0;
`;

const EmptyFavoritesTitle = styled.div`
    font-weight: 700;
    font-size: 1rem;
    text-align: center;
    margin-bottom: 12px;
    color: #222;
`;

const EmptyFavoritesSubtitle = styled.div`
    color: #9e9e9e;
    font-size: 1rem;
    margin-bottom: 32px;
    text-align: center;
`;

const EmptyFavoritesButtonWrapper = styled.div`
    width: 320px;
    max-width: 100%;
`;
